import { useState, useEffect, useMemo } from 'react';

import { fg } from '@confluence/feature-gating';
import {
	confluenceLocalStorageInstance as localStorage,
	confluenceSessionStorageInstance as sessionStorage,
} from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

const ADMIN_ANNOUNCEMENT_BANNER_DISMISS_STATE_KEY = 'admin.announcement.banner';
const PERSISTED_ADMIN_ANNOUNCEMENT_BANNER_DISMISS_STATE_KEY =
	PERSISTED_KEYS_ON_SERVER.PERSISTED_ADMIN_ANNOUNCEMENT_BANNER;

type DismissalStateParam = {
	isLicensed: boolean;
	id?: string;
	updatedAt?: string;
};

export const getIsDismissed = ({
	cachedUpdatedAt,
	updatedAt,
}: {
	cachedUpdatedAt?: string;
	updatedAt?: string;
}) => {
	if (!updatedAt) return false;
	return cachedUpdatedAt === updatedAt;
};

export const useDismissalState = ({ isLicensed, id, updatedAt }: DismissalStateParam) => {
	const { storage, dismissStateKey, initialUpdatedAt } = useMemo(() => {
		if (!isLicensed) {
			return {
				storage: sessionStorage,
				dismissStateKey: ADMIN_ANNOUNCEMENT_BANNER_DISMISS_STATE_KEY,
				initialUpdatedAt: fg('cc_aab_persist_session_dismissal')
					? sessionStorage?.getItem(ADMIN_ANNOUNCEMENT_BANNER_DISMISS_STATE_KEY)?.updatedAt
					: undefined,
			};
		}

		return {
			storage: localStorage,
			dismissStateKey: PERSISTED_ADMIN_ANNOUNCEMENT_BANNER_DISMISS_STATE_KEY,
			initialUpdatedAt: localStorage?.getItem(PERSISTED_ADMIN_ANNOUNCEMENT_BANNER_DISMISS_STATE_KEY)
				?.updatedAt,
		};
	}, [isLicensed]);

	const [isDismissed, setIsDismissed] = useState<boolean | undefined>(
		getIsDismissed({ cachedUpdatedAt: initialUpdatedAt, updatedAt }),
	);

	useEffect(() => {
		if (updatedAt) {
			const cachedUpdatedAt = storage.getItem(dismissStateKey)?.updatedAt;
			setIsDismissed(getIsDismissed({ cachedUpdatedAt, updatedAt }));
		}
	}, [dismissStateKey, storage, updatedAt]);

	const onDismiss = () => {
		storage.setItem(dismissStateKey, { id, updatedAt });
		const cachedUpdatedAt = storage.getItem(dismissStateKey)?.updatedAt;
		setIsDismissed(getIsDismissed({ cachedUpdatedAt, updatedAt }));
	};

	return { isDismissed, onDismiss };
};
